import {
  Alert,
  Button,
  Checkbox,
  CheckboxGroup,
  Flex,
  Heading,
  IconArrowStartLine,
  List,
  Text,
  ToggleDetails,
  View,
} from "@instructure/ui";
import { useTranslation } from "react-i18next";
import { useCallback, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { useLocation, useNavigate } from "react-router";
import { AssessmentPlanService } from "../../../services/AssessmentService";
import { Files } from "../../../services/FilesService";
import { fileWriter } from "../../../utils/FileUtils";
import { ApDataContext } from "../../../views/Plan";
import { ERROR, useNotification } from "../../CustomHooks/NotificationProvider";
import CustomRTE, { EMPTY_RTE } from "../../CustomTextArea/CustomRTE";
import { DialogYesNo } from "../../DialogYesNo/DialogYesNo";
import TemplateSelector from "../../PlanTemplates/TemplateSelector";
import { CycleSelect } from "../../Selectors/CyclesSelector";
import ResultsPreview from "./ResultsPreview/ResultsPreview";
import ReviewersCard from "./Reviewers/ReviewersCard";
import TemplateSectionCard from "./Sections/TemplateSectionCard";
import {
  CANVAS,
  EMBEDDED,
  EXTERNAL,
  QUALITATIVE,
  emailVerification,
} from "./utils";

const CreatePlanFromTemplate = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const showNotification = useNotification();
  const [selectedTemplate, setSelectedTemplate] = useState();
  const [loadingTemplate, setLoadingTemplate] = useState(false);

  const [selectedCycle, setSelectedCycle] = useState({});
  const [mission, setMission] = useState(EMPTY_RTE);
  const [approver, setApprover] = useState("");
  const [reviewers, setReviewers] = useState([]);
  const [sections, setSections] = useState([]);

  const [enableCycleTemplate, setEnableCycleTemplate] = useState(true);
  const [confirmBackDialog, setConfirmBackDialog] = useState(false);
  const [createConfirm, setCreateConfirm] = useState(false);

  const handleCycleSelect = (cycleId) => {
    setSelectedCycle(
      location.state.availCycles.find((cycle) => cycle.id === cycleId)
    );
  };

  async function handleTemplateSelect(template) {
    try {
      setLoadingTemplate(true);
      const updatedSections = await Promise.all(
        template.sections.map(async (section, i) => {
          section.sequence = i;
          section.approvers = "";
          section.reviewers = [];
          delete section.id;

          const updatedMeasures = await Promise.all(
            section.measures.map(async (measure) => {
              const updatedMeasure = { ...measure };
              if (measure.description_key) {
                const description = await Files.getFileContent(
                  "response.html",
                  "templateAssignmentDescription",
                  measure.description_key
                );
                updatedMeasure.description = description.content;
              }
              return updatedMeasure;
            })
          );

          return { ...section, measures: updatedMeasures };
        })
      );
      const updatedTemplate = { ...template, sections: updatedSections };

      setSelectedTemplate(updatedTemplate);
      setSections(updatedSections);
    } catch {
      showNotification(
        t("notifications.general.fetchError", { obj: template.name }),
        ERROR
      );
    } finally {
      setLoadingTemplate(false);
    }
  }

  const handleCreateMeasure = useCallback(
    (newMeasure, sectionIndex, measureIndex) => {
      setEnableCycleTemplate(false);
      newMeasure.sequence = measureIndex;
      const newSections = [...sections];
      newSections[sectionIndex].measures[measureIndex] = newMeasure;
      setSections(newSections);
      let reviewers = [];
      newSections.forEach((section) => {
        section.measures.forEach((measure) => {
          if (measure.type === QUALITATIVE && !!measure.ddl_id) {
            reviewers = reviewers.concat(measure.assignees.split(","));
            reviewers = Array.from(new Set(reviewers));
          }
        });
      });
    },
    [sections]
  );

  const formVerification = () => {
    if (!approver) {
      showNotification(
        t("assessment.planRequirements.approverRequired"),
        ERROR
      );
      return false;
    }
    if (!emailVerification(approver)) {
      showNotification(t("assessment.planRequirements.invalidApprover"), ERROR);
      return false;
    }
    if (reviewers.length === 0) {
      showNotification(
        t("assessment.planRequirements.reviewersRequired"),
        ERROR
      );
      return false;
    }
    if (selectedTemplate.include_mission && mission === EMPTY_RTE) {
      showNotification(t("assessment.planRequirements.missionRequired"), ERROR);
      return false;
    }
    let missingSectionApprover = false;
    let missingSectionReviewer = false;
    sections.forEach((section) => {
      if (!section.approvers) {
        missingSectionApprover = true;
      }
      if (section.reviewers.length === 0) {
        missingSectionReviewer = true;
      }
    });
    if (missingSectionApprover) {
      showNotification(
        t("assessment.planRequirements.sectionApproversRequired"),
        ERROR
      );
      return false;
    }
    if (missingSectionReviewer) {
      showNotification(
        t("assessment.planRequirements.sectionReviewersRequired"),
        ERROR
      );
      return false;
    }
    let missingMeasure = false;
    sections.forEach((section) => {
      section.measures.forEach((measure) => {
        if (!measure.id && !measure.ddl_id) {
          missingMeasure = true;
        }
      });
    });
    if (missingMeasure) {
      showNotification(
        t("assessment.planRequirements.measuresRequired"),
        ERROR
      );
      return false;
    }
    return true;
  };

  const dedupPurlReviewers = (purlReviewers) => {
    return Array.from(new Set(purlReviewers));
  };

  const handleCreatePlan = async () => {
    const plan = {
      cycleId: selectedCycle.id,
      sections: [],
      approvers: approver,
      reviewers: reviewers.toString(),
      purlReviewers: "",
      fromTemplate: true,
      includeGoalsObjs: selectedTemplate.include_goals_objs,
    };

    let purlReviewers = [approver, ...reviewers];
    const planSections = JSON.parse(JSON.stringify(sections));
    planSections.forEach((section) => {
      purlReviewers = purlReviewers.concat([
        section.approvers,
        ...section.reviewers,
      ]);
      section.assessmentList = [];
      section.qualitativeMeasures = [];
      section.embeddedDatalinks = [];
      section.measures.forEach((measure) => {
        if (measure.type === CANVAS || measure.type === EXTERNAL) {
          section.assessmentList.push(measure.id);
        } else if (measure.type === EMBEDDED) {
          section.embeddedDatalinks.push(measure.ddl_id);
        } else if (measure.type === QUALITATIVE) {
          section.qualitativeMeasures.push(measure.ddl_id);
          purlReviewers = purlReviewers.concat(measure.assignees.split(","));
        }
      });
      section.reviewers = section.reviewers.toString();
      delete section.measures;
    });
    plan.sections = planSections;
    plan.purlReviewers = dedupPurlReviewers(purlReviewers).toString();
    const obj = { assessmentPlan: plan };
    await AssessmentPlanService.createAssessmentPlan(obj)
      .then(async (newPlan) => {
        if (selectedTemplate.include_mission) {
          await handleSavePlanMission(newPlan.id);
        }
        showNotification(
          t("notifications.general.createSuccess", { obj: "Assessment Plan" })
        );
        setCreateConfirm(false);
        navigate("/plan");
      })
      .catch(() => {
        showNotification(
          t("notifications.general.createError", { obj: "Assessment Plan" }),
          ERROR
        );
      });
  };

  const handleUpdateApprover = useCallback((newApprover) => {
    setApprover(newApprover);
  }, []);

  const handleSavePlanMission = async (planId) => {
    const mission_file = fileWriter(mission, "response.html", "text/html");
    await Files.saveFile(mission_file, "mission", planId);
  };

  const handleAddReviewer = useCallback((newReviewer) => {
    setReviewers((prevReviewers) => [...prevReviewers, newReviewer]);
  }, []);

  const handleRemoveReviewer = useCallback((index) => {
    setReviewers((prevReviewers) =>
      prevReviewers.filter((_, i) => i !== index)
    );
  }, []);

  return (
    <div style={{ margin: "-0.75rem", height: "calc(100vh - 1.5rem)" }}>
      <Flex direction="column" height="100%">
        <Flex.Item padding="small">
          <div style={{ borderBottom: "1px solid #ccc" }}>
            <Heading as="h1" level="h2">
              {t("assessment.createPlanFromTemplate")}
            </Heading>
          </div>
        </Flex.Item>
        <Flex.Item shouldGrow shouldShrink>
          <Flex height="100%" alignItems="start">
            <Flex.Item size="35%" overflowY="auto" height="100%">
              <View
                as="div"
                borderWidth="0 small 0 0"
                height="100%"
                padding="0 small 0 0"
              >
                <Flex height="100%" direction="column">
                  <Flex.Item padding="0 small small small">
                    <CycleSelect
                      selectHandler={handleCycleSelect}
                      selectedCycle={selectedCycle}
                      cycles={location.state.availCycles}
                      interaction={enableCycleTemplate}
                    />
                  </Flex.Item>
                  <Flex.Item padding="0 small small small">
                    <TemplateSelector
                      label={t("planTemplates.template")}
                      selectHandler={handleTemplateSelect}
                      interaction={!!selectedCycle.id && enableCycleTemplate}
                      selectedItemId={selectedTemplate?.id}
                    />
                  </Flex.Item>
                  {!!selectedTemplate && (
                    <Flex.Item margin="x-small 0" padding="0 small small small">
                      <View as="div" borderWidth="small 0 0 0" />
                    </Flex.Item>
                  )}
                  {!!selectedTemplate && (
                    <Flex.Item padding="0 small medium small">
                      <CheckboxGroup
                        name="headers"
                        description={t("planTemplates.include") + ":"}
                        layout="columns"
                        value={[
                          ...(selectedTemplate.include_mission
                            ? ["mission"]
                            : []),
                          ...(selectedTemplate.include_goals_objs
                            ? ["goalsObjs"]
                            : []),
                        ]}
                        onChange={() => {}}
                        disabled
                      >
                        <Checkbox
                          label={t("assessment.mission")}
                          value="mission"
                        />
                        <Checkbox
                          label={t("dashboard.goalsAndObjectives")}
                          value="goalsObjs"
                        />
                      </CheckboxGroup>
                    </Flex.Item>
                  )}
                  {!!selectedTemplate && (
                    <Flex.Item padding="0 small small small" shouldGrow>
                      <ReviewersCard
                        handleUpdateApprover={handleUpdateApprover}
                        handleAddReviewer={handleAddReviewer}
                        handleRemoveReviewer={handleRemoveReviewer}
                      />
                    </Flex.Item>
                  )}
                </Flex>
              </View>
            </Flex.Item>
            <Flex.Item
              size="65%"
              overflowY="auto"
              height="100%"
              padding="0 small small medium"
            >
              {loadingTemplate ? (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "1.5rem",
                    height: "100%",
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <Skeleton height="100%" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <Skeleton height="100%" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <Skeleton height="100%" />
                  </div>
                </div>
              ) : !selectedTemplate ? (
                <Alert>{t("assessment.selectCycleAndTemplate")}</Alert>
              ) : (
                <Flex wrap="wrap" gap="small">
                  {!!selectedTemplate.include_mission && (
                    <Flex.Item size="100%">
                      <CustomRTE
                        id="mission-desc"
                        label={
                          <Heading level="h2">
                            {t("assessment.mission")}
                          </Heading>
                        }
                        defaultValue={mission}
                        onChange={(newValue) => setMission(newValue)}
                      />
                    </Flex.Item>
                  )}
                  {!!selectedTemplate.include_goals_objs && (
                    <Flex.Item size="100%">
                      <ToggleDetails
                        summary={
                          <Heading level="h2">
                            {t("dashboard.goalsAndObjectives")}
                          </Heading>
                        }
                        defaultExpanded
                      >
                        <List isUnstyled>
                          {selectedCycle.goals.map((goal) => (
                            <List.Item key={goal.id}>
                              <Text weight="bold">{goal.description}</Text>
                              <List>
                                {goal.objectives.map((objective) => (
                                  <List.Item key={objective.id}>
                                    {objective.description}
                                  </List.Item>
                                ))}
                              </List>
                            </List.Item>
                          ))}
                        </List>
                      </ToggleDetails>
                    </Flex.Item>
                  )}
                  <Flex.Item size="100%">
                    <Heading level="h2">
                      {t("assessment.sections.sections")}:
                    </Heading>
                    {sections.map((section, key) => (
                      <ApDataContext.Provider
                        key={`section-${key}`}
                        value={{
                          sectionIndex: key,
                          selectedCycle,
                          handleCreateMeasure,
                          setSections,
                          extraData: location.state,
                        }}
                      >
                        <TemplateSectionCard section={section} />
                      </ApDataContext.Provider>
                    ))}
                  </Flex.Item>
                </Flex>
              )}
            </Flex.Item>
          </Flex>
        </Flex.Item>
        <Flex.Item padding="small">
          <View as="div" borderWidth="small 0 0 0">
            <Flex margin="small 0 0 0" gap="small" wrap="wrap">
              <Flex.Item shouldGrow>
                <Button
                  renderIcon={IconArrowStartLine}
                  onClick={() => {
                    if (!enableCycleTemplate) {
                      setConfirmBackDialog(true);
                    } else {
                      navigate("/plan");
                    }
                  }}
                >
                  {t("global.goBack")}
                </Button>
              </Flex.Item>
              <Flex.Item>
                <PreviewResultsButton
                  selectedCycle={selectedCycle}
                  sections={sections}
                  selectedTemplate={selectedTemplate}
                  mission={mission}
                />
              </Flex.Item>
              <Flex.Item>
                <Button
                  color="primary"
                  onClick={() => {
                    if (formVerification()) {
                      setCreateConfirm(true);
                    }
                  }}
                  interaction={selectedTemplate ? "enabled" : "disabled"}
                >
                  {t("academicHierarchy.create")}
                </Button>
              </Flex.Item>
            </Flex>
          </View>
        </Flex.Item>
      </Flex>
      <DialogYesNo
        message={t("assessment.goBackConfirm")}
        openStatus={confirmBackDialog}
        onClickNo={() => setConfirmBackDialog(false)}
        onClickYes={() => {
          setConfirmBackDialog(false);
          navigate("/plan");
        }}
      />
      <DialogYesNo
        message={t("assessment.createFromPlanConfirm")}
        openStatus={createConfirm}
        onClickNo={() => setCreateConfirm(false)}
        onClickYes={handleCreatePlan}
      />
    </div>
  );
};

export default CreatePlanFromTemplate;

const PreviewResultsButton = ({
  selectedCycle,
  sections,
  selectedTemplate,
  mission,
}) => {
  const { t } = useTranslation();
  const [openPreview, setOpenPreview] = useState(false);

  return (
    <>
      <Button
        onClick={() => setOpenPreview(true)}
        interaction={selectedTemplate ? "enabled" : "disabled"}
      >
        {t("results.previewResults")}
      </Button>
      <ResultsPreview
        open={openPreview}
        setOpen={setOpenPreview}
        sections={sections}
        data={{
          mission: selectedTemplate?.include_mission ? mission : null,
          goalsObjs: selectedTemplate?.include_goals_objs
            ? selectedCycle
            : null,
        }}
      />
    </>
  );
};
